import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col, Card, Table, Form, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { Product, ProductFilters, ProductDetails } from '../types';
import ShoppingCart from '../components/ShoppingCart';
import ImageCarousel from '../components/ImageCarousel';
import StarRating from '../components/StarRating';
import { getApiUrl } from '../config/api';
import { useCart } from '../context/CartContext';

interface ProductsPageProps {
  productType?: 'car_mat' | 'trunk_mat' | 'other';
}

const ProductsPage: React.FC<ProductsPageProps> = ({ productType }) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ProductFilters>({});
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [loadingDetails, setLoadingDetails] = useState<Set<string>>(new Set());
  const [productDetails, setProductDetails] = useState<Map<string, ProductDetails>>(new Map());
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMorePages, setHasMorePages] = useState<boolean>(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [totalPages, setTotalPages] = useState<number>(0);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { addProductToCart } = useCart();

  const fetchProductDetails = useCallback(async (productId: string): Promise<ProductDetails | null> => {
    try {
      const url = getApiUrl(`/api/web/products/details/${productId}?language=${i18n.language}`);
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        console.error(`Failed to fetch product details: ${response.statusText}`);
        return null;
      }
      
      const data = await response.json();
      return data?.data || null;
    } catch (err) {
      console.error('Error fetching product details:', err);
      return null;
    }
  }, []);

  const fetchProducts = useCallback(async (
    searchFilters: ProductFilters = {}, 
    page: number = 1, 
    append: boolean = false
  ) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setCurrentPage(1);
    }
    setError(null);
    
    try {
      const queryParams = new URLSearchParams();
      
      // Add pagination parameters
      queryParams.append('page', page.toString());
      queryParams.append('limit', '50'); // 50 items per page
      
      // Add product type filter if specified
      if (productType) {
        queryParams.append('product_type', productType);
      }
      
      if (searchFilters.make) queryParams.append('make', searchFilters.make);
      if (searchFilters.model) queryParams.append('model', searchFilters.model);
      if (searchFilters.year_range) queryParams.append('year', searchFilters.year_range);
      if (searchFilters.prod_title) queryParams.append('prod_title', searchFilters.prod_title);
      if (searchFilters.prod_ref) queryParams.append('prod_ref', searchFilters.prod_ref);
      if (searchFilters.pattern_title) queryParams.append('pattern_title', searchFilters.pattern_title);
      if (searchFilters.material_title) queryParams.append('material_title', searchFilters.material_title);
      
      // Add language parameter
      queryParams.append('language', i18n.language);
      
      const url = getApiUrl(`/api/web/products/search?${queryParams.toString()}`);
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.statusText}`);
      }
      
      const data = await response.json();
      const newProducts = Array.isArray(data?.data?.products) ? data.data.products : [];
      
      if (append) {
        setProducts(prev => [...prev, ...newProducts]);
        setCurrentPage(page);
      } else {
        setProducts(newProducts);
      }
      
      // Update pagination info
      const pagination = data?.data?.pagination;
      if (pagination) {
        setTotalPages(pagination.total_pages || 0);
        setHasMorePages(page < (pagination.total_pages || 0));
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch products');
      if (!append) {
        setProducts([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [productType]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Reload products when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      // Reset state and reload products
      setCurrentPage(1);
      setHasMorePages(true);
      setExpandedRows(new Set());
      setLoadingDetails(new Set());
      setProductDetails(new Map());
      
      // Reload products with new language
      fetchProducts(filters, 1, false);
    };

    // Listen for language change events
    i18n.on('languageChanged', handleLanguageChange);

    // Cleanup listener
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [filters, fetchProducts]);

  // Reset filters when productType changes (switching between modules)
  useEffect(() => {
    setFilters({});
    setCurrentPage(1);
    setHasMorePages(true);
    setExpandedRows(new Set());
    setLoadingDetails(new Set());
    setProductDetails(new Map());
  }, [productType]);

  // Function to load more products
  const loadMoreProducts = useCallback(async () => {
    if (!hasMorePages || loadingMore || loading) return;
    
    const nextPage = currentPage + 1;
    await fetchProducts(filters, nextPage, true);
  }, [hasMorePages, loadingMore, loading, currentPage, filters, fetchProducts]);

  // Scroll detection for infinite scroll
  const handleScroll = useCallback((event: Event) => {
    const target = event.target as HTMLDivElement;
    if (!target) return;
    
    const { scrollTop, scrollHeight, clientHeight } = target;
    const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 100; // 100px threshold
    
    if (scrolledToBottom && hasMorePages && !loadingMore && !loading) {
      loadMoreProducts();
    }
  }, [hasMorePages, loadingMore, loading, loadMoreProducts]);

  // Add scroll listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handleFilterChange = (field: keyof ProductFilters, value: string) => {
    const newFilters = { ...filters, [field]: value || undefined };
    setFilters(newFilters);
    
    // Reset pagination state when filters change
    setCurrentPage(1);
    setHasMorePages(true);
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      fetchProducts(newFilters, 1, false);
    }, 600);
  };

  const toggleRowExpansion = async (productId: string) => {
    if (expandedRows.has(productId)) {
      // Close the current row
      const newExpandedRows = new Set(expandedRows);
      newExpandedRows.delete(productId);
      setExpandedRows(newExpandedRows);
    } else {
      // Close all other rows and open this one
      const newExpandedRows = new Set([productId]);
      setExpandedRows(newExpandedRows);
      
      // Check if we already have details for this product
      if (!productDetails.has(productId)) {
        // Add to loading state
        const newLoadingDetails = new Set(loadingDetails);
        newLoadingDetails.add(productId);
        setLoadingDetails(newLoadingDetails);
        
        // Fetch product details
        const details = await fetchProductDetails(productId);
        
        // Remove from loading state
        const updatedLoadingDetails = new Set(loadingDetails);
        updatedLoadingDetails.delete(productId);
        setLoadingDetails(updatedLoadingDetails);
        
        // Store details if successful
        if (details) {
          const newProductDetails = new Map(productDetails);
          newProductDetails.set(productId, details);
          setProductDetails(newProductDetails);
        }
      }
    }
  };

  const handleAddToCart = (product: Product) => {
    addProductToCart(product, 1);
  };

  // Get dynamic title based on product type
  const getPageTitle = () => {
    switch (productType) {
      case 'car_mat':
        return t('products.carMats');
      case 'trunk_mat':
        return t('products.trunkMats');
      case 'other':
        return t('products.otherProducts');
      default:
        return t('products.title');
    }
  };

  return (
    <main className="container py-4">
      <Row>
        {/* Left side: Products table */}
        <Col lg={8} className="selection-panel">
          <Card className="d-flex flex-column" style={{ height: 'calc(100vh - 140px)' }}>
            <Card.Header className="flex-shrink-0 bg-dark text-white">
              <h5 className="mb-0">{getPageTitle()}</h5>
            </Card.Header>
            <Card.Body className="flex-grow-1 overflow-hidden p-0">
              {error && (
                <Alert variant="danger" className="m-3 mb-0">
                  {error}
                </Alert>
              )}
              
              <div 
                ref={scrollContainerRef}
                className="table-responsive" 
                style={{ height: '100%', overflowY: 'auto' }}
              >
                <Table striped hover className="mb-0 table-sm table-compact">
                <thead className="sticky-top bg-white" style={{ top: '0', zIndex: 10 }}>
                  <tr className="table-header">
                    {productType === 'other' ? (
                      // Simple table for other products: code, description, price, info, order
                      <>
                        <th style={{ width: '120px', verticalAlign: 'top' }}>
                          {t('products.table.code')}
                          <Form.Control
                            type="text"
                            size="sm"
                            placeholder={t('products.table.filters.code')}
                            className="mt-1 table-filter-input"
                            value={filters.prod_ref || ''}
                            onChange={(e) => handleFilterChange('prod_ref', e.target.value)}
                          />
                        </th>
                        <th style={{ verticalAlign: 'top' }}>
                          {t('products.table.description')}
                          <Form.Control
                            type="text"
                            size="sm"
                            placeholder={t('products.table.filters.description')}
                            className="mt-1 table-filter-input"
                            value={filters.prod_title || ''}
                            onChange={(e) => handleFilterChange('prod_title', e.target.value)}
                          />
                        </th>
                        <th style={{ width: '80px', verticalAlign: 'top' }}>
                          {t('products.table.price')}
                        </th>
                        <th style={{ width: '60px', verticalAlign: 'top' }}>
                          {t('products.table.info')}
                        </th>
                        <th style={{ width: '80px', verticalAlign: 'top' }}>
                          {t('products.table.order')}
                        </th>
                      </>
                    ) : (
                      // Full table for mats: code, model, quality, year, price, info, order
                      <>
                        <th style={{ width: '120px', verticalAlign: 'top' }}>
                          {t('products.table.code')}
                          <Form.Control
                            type="text"
                            size="sm"
                            placeholder={t('products.table.filters.code')}
                            className="mt-1 table-filter-input"
                            value={filters.prod_ref || ''}
                            onChange={(e) => handleFilterChange('prod_ref', e.target.value)}
                          />
                        </th>
                        <th style={{ width: '300px', verticalAlign: 'top' }}>
                          {t('products.table.model')}
                          <Form.Control
                            type="text"
                            size="sm"
                            placeholder={t('products.table.filters.model')}
                            className="mt-1 table-filter-input"
                            value={filters.pattern_title || ''}
                            onChange={(e) => handleFilterChange('pattern_title', e.target.value)}
                          />
                        </th>
                        <th style={{ width: '100px', verticalAlign: 'top' }}>
                          {t('products.table.quality')}
                          <Form.Control
                            type="text"
                            size="sm"
                            placeholder={t('products.table.filters.quality')}
                            className="mt-1 table-filter-input"
                            value={filters.material_title || ''}
                            onChange={(e) => handleFilterChange('material_title', e.target.value)}
                          />
                        </th>
                        <th style={{ width: '120px', verticalAlign: 'top' }}>
                          {t('products.table.buildYear')}
                          <Form.Control
                            type="text"
                            size="sm"
                            placeholder={t('products.table.filters.buildYear')}
                            className="mt-1 table-filter-input"
                            value={filters.year_range || ''}
                            onChange={(e) => handleFilterChange('year_range', e.target.value)}
                          />
                        </th>
                        <th style={{ width: '80px', verticalAlign: 'top' }}>
                          {t('products.table.price')}
                        </th>
                        <th style={{ width: '60px', verticalAlign: 'top' }}>
                          {t('products.table.info')}
                        </th>
                        <th style={{ width: '80px', verticalAlign: 'top' }}>
                          {t('products.table.order')}
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={productType === 'other' ? 5 : 7} className="text-center py-4">
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="ms-2">{t('products.loading')}</span>
                      </td>
                    </tr>
                  ) : products.length === 0 ? (
                    <tr>
                      <td colSpan={productType === 'other' ? 5 : 7} className="text-center py-4 text-muted">
                        {t('products.noProducts')}
                      </td>
                    </tr>
                  ) : (
                    products.map((product) => (
                      <React.Fragment key={product.id}>
                        {productType === 'other' ? (
                          // Row for other products with expandable details: code, description, price, info, order
                          <>
                            <tr className="line-height-tight">
                              <td className="table-cell-bold">{product.prod_ref}</td>
                              <td className="table-cell">{product.prod_title}</td>
                              <td className="table-cell-bold">€{product.prod_price.toFixed(2)}</td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => toggleRowExpansion(product.id)}
                                  style={{ border: 'none', background: 'none', color: '#007bff' }}
                                >
                                  <i className={`fas fa-info-circle ${expandedRows.has(product.id) ? 'text-primary' : ''}`}></i>
                                </button>
                              </td>
                              <td>
                                {product.prod_price > 0 && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleAddToCart(product)}
                                    style={{ border: 'none', background: 'none', color: '#007bff' }}
                                    title="Bestellen"
                                  >
                                    <i className="fas fa-cart-plus"></i>
                                  </button>
                                )}
                              </td>
                            </tr>
                            {expandedRows.has(product.id) && (
                              <tr>
                                <td colSpan={5} className="bg-light p-3">
                                  {loadingDetails.has(product.id) ? (
                                    <div className="text-center py-3">
                                      <div className="spinner-border spinner-border-sm me-2" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                      </div>
                                      Loading product details...
                                    </div>
                                  ) : (
                                    (() => {
                                      const details = productDetails.get(product.id);
                                      if (!details) {
                                        return (
                                          <div className="text-center py-3 text-muted">
                                            <i className="fas fa-exclamation-triangle me-2"></i>
                                            {t('products.details.loadingError')}
                                          </div>
                                        );
                                      }

                                      // Simple view for other products: only description and images
                                      return (
                                        <div className="row">
                                          {/* Left side: Image carousel (50%) */}
                                          <div className="col-md-6">
                                            {details.images && details.images.length > 0 ? (
                                              <ImageCarousel images={details.images} />
                                            ) : (
                                              <div className="text-center py-5 text-muted">
                                                <i className="fas fa-image fa-3x mb-3"></i>
                                                <p>Geen afbeeldingen beschikbaar</p>
                                              </div>
                                            )}
                                          </div>

                                          {/* Right side: Product description (50%) */}
                                          <div className="col-md-6">
                                            <h4 className="mb-3 fw-bold">{details.title}</h4>
                                            <div className="product-details">
                                              <div className="mb-2">
                                                <strong>{t('products.table.description')}:</strong>
                                                <p className="mt-2">{details.title}</p>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })()
                                  )}
                                </td>
                              </tr>
                            )}
                          </>
                        ) : (
                          // Full row for mats with expandable details
                          <>
                            <tr className="line-height-tight">
                              <td className="table-cell-bold">{product.prod_ref}</td>
                              <td className="table-cell" style={{ paddingRight: '8px' }}>
                                <div className="pattern-title">{product.pattern_title}</div>
                                {product.pattern_exceptions && (
                                  <div className="pattern-exceptions">
                                    {product.pattern_exceptions}
                                  </div>
                                )}
                              </td>
                              <td className="table-cell">{product.material_title}</td>
                              <td className="table-cell">{product.year_range}</td>
                              <td className="table-cell-bold">€{product.prod_price.toFixed(2)}</td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => toggleRowExpansion(product.id)}
                                  style={{ border: 'none', background: 'none', color: '#007bff' }}
                                >
                                  <i className={`fas fa-info-circle ${expandedRows.has(product.id) ? 'text-primary' : ''}`}></i>
                                </button>
                              </td>
                              <td>
                                {product.prod_price > 0 && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleAddToCart(product)}
                                    style={{ border: 'none', background: 'none', color: '#007bff' }}
                                    title="Bestellen"
                                  >
                                    <i className="fas fa-cart-plus"></i>
                                  </button>
                                )}
                              </td>
                            </tr>
                            {expandedRows.has(product.id) && (
                              <tr>
                                <td colSpan={7} className="bg-light p-3">
                              {loadingDetails.has(product.id) ? (
                                <div className="text-center py-3">
                                  <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                  Loading product details...
                                </div>
                              ) : (
                                (() => {
                                  const details = productDetails.get(product.id);
                                  if (!details) {
                                    return (
                                      <div className="text-center py-3 text-muted">
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        {t('products.details.loadingError')}
                                      </div>
                                    );
                                  }
                                  
                                  return (
                                    <div className="row">
                                      {/* Left side: Image carousel (50%) */}
                                      <div className="col-md-6">
                                        <ImageCarousel images={details.images} />
                                      </div>
                                      
                                      {/* Right side: Product details (50%) */}
                                      <div className="col-md-6">
                                        <h4 className="mb-3 fw-bold">{details.quality_title}</h4>
                                        
                                        <div className="product-details">
                                          <div className="mb-2">
                                            <strong>{t('products.details.material')}:</strong> {details.material.name}
                                          </div>
                                          
                                          <div className="mb-2">
                                            <strong>{t('products.details.thickness')}:</strong> {details.material.thickness}
                                          </div>
                                          
                                          <div className="mb-2">
                                            <strong>{t('products.details.backing')}:</strong> {details.material.backing}
                                          </div>
                                          
                                          <div className="mb-2">
                                            <strong>{t('products.details.recycled')}:</strong> {details.material.recycled}
                                          </div>
                                          
                                          <div className="mb-2">
                                            <strong>{t('products.details.quality')}:</strong>
                                            <div className="mt-1">
                                              <StarRating stars={details.material.quality_stars} showScore={true} />
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()
                              )}
                                </td>
                              </tr>
                            )}
                          </>
                        )}
                      </React.Fragment>
                    ))
                  )}
                  {/* Loading more indicator */}
                  {loadingMore && (
                    <tr>
                      <td colSpan={productType === 'other' ? 5 : 7} className="text-center py-3">
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="text-muted">{t('products.loadingMore')}</span>
                      </td>
                    </tr>
                  )}
                  {/* End of results indicator */}
                  {!loading && !loadingMore && !hasMorePages && products.length > 0 && (
                    <tr>
                      <td colSpan={productType === 'other' ? 5 : 7} className="text-center py-3 text-muted">
                        <small>{t('products.noMoreProducts')}</small>
                      </td>
                    </tr>
                  )}
                </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Right side: Shopping cart */}
        <Col lg={4} className="cart-panel">
          <ShoppingCart />
        </Col>
      </Row>
    </main>
  );
};

export default ProductsPage;